import type { FC } from 'react'
import React from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import Tooltip from '@material-ui/core/Tooltip'

import { Tooltip as CustomTooltip } from '@homeviews/shared-ui'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginTop: 14,
      '& .l-chartLabelItem': {
        display: 'flex',
        alignItems: 'center',
        marginRight: 19,
        marginTop: 10,
        pointerEvents: 'none',
        '& p': {
          fontSize: 12,
          fontWeight: 400,
          letterSpacing: '-0.11px',
          lineHeight: '15px',
          margin: 0,
          whiteSpace: 'nowrap',
        },
      },
      '& .l-chartLabelItem--disabled': {
        opacity: '0.5!important',
        '& .l-chartLabelItem__innerShape': {
          display: 'none',
        },
      },
      '& .l-chartLabelItem--clickable': {
        pointerEvents: 'all',
        '&:hover': {
          cursor: 'pointer',
        },
        '& .l-chartLabelItem__shape': {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },

        '& .l-chartLabelItem__innerShape': {
          width: 5,
          height: 5,
          borderRadius: '50%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        },
      },
      '& .l-chartLabelItem__shape': {
        width: 12,
        height: 12,
        borderRadius: '50%',
        borderWidth: 2,
        borderStyle: 'solid',
        marginRight: 5,
        flexShrink: 0,
      },
    },
    vertical: {
      flexDirection: 'column',
      marginTop: 0,
      '& .l-chartLabelItem': {
        margin: 0,
      },
      '& .l-chartLabelItem + .l-chartLabelItem': {
        marginTop: 5,
      },
    },
    tooltipWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginRight: 19,
      marginTop: 10,
      marginLeft: -15,
    },
  }),
)

const TooltipWrapper = React.forwardRef((props: any, ref: any) => {
  //  Spread the props to the underlying DOM element.
  return (
    <div {...props} ref={ref}>
      {props.children}
    </div>
  )
})

interface ItemProps {
  text: string
  id: string
  textColor?: string
  shapeColor: string
  onClick?: (id: string) => void
  disabled?: boolean
  tooltipText?: string
}

export const ChartLegendItem: FC<ItemProps> = ({
  text,
  id,
  textColor = '#4E516B',
  shapeColor = '#4E516B',
  onClick,
  disabled,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(id)
    }
  }

  return (
    <Tooltip
      title="Click to toggle on and off"
      open={disabled ? false : undefined}
    >
      <TooltipWrapper
        className={clsx(
          'l-chartLabelItem',
          disabled && 'l-chartLabelItem--disabled',
          onClick && 'l-chartLabelItem--clickable',
        )}
        onClick={handleClick}
      >
        <>
          <div
            className="l-chartLabelItem__shape"
            style={{
              borderColor: shapeColor,
            }}
          >
            {onClick && (
              <span
                className={clsx('l-chartLabelItem__innerShape')}
                style={{
                  backgroundColor: shapeColor,
                }}
              />
            )}
          </div>
          <p
            style={{
              color: textColor,
            }}
          >
            {text}
          </p>
        </>
      </TooltipWrapper>
    </Tooltip>
  )
}

interface IProps {
  data: any
  direction?: 'vertical' | 'horizontal'
  onClick?: (id: string) => void
  disabledDatasets?: string[]
}

const ChartLegend: FC<IProps> = ({
  data,
  direction = 'horizontal',
  onClick,
}) => {
  const classes = useStyles()
  return (
    <div
      className={clsx(
        'l-chartLegend',
        classes.root,
        direction === 'vertical' && classes.vertical,
      )}
    >
      {data
        .filter((item) => item)
        .map(({ id, text, textColor, shapeColor, disabled, tooltipText }) => (
          <div
            style={{
              display: 'flex',
            }}
            key={text}
          >
            <ChartLegendItem
              id={id}
              text={text}
              textColor={textColor}
              shapeColor={shapeColor}
              onClick={onClick}
              disabled={disabled}
              tooltipText={tooltipText}
            />
            {tooltipText && (
              <div className={classes.tooltipWrapper}>
                <CustomTooltip text={tooltipText} />
              </div>
            )}
          </div>
        ))}
    </div>
  )
}

export default ChartLegend
